import {
  CashRegisterFormQuestionOnEvent,
  EventFormQuestion,
  EventFormQuestionFilters,
  EventFormQuestionOnEvent,
  PaginatedResponse,
  BatchEventFormQuestionsResponse,
} from "@ac-expo/api-types";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { eventFormQuestionsQueryKeys } from "../query-keys/event-form-questions.query-keys";

async function getEventFormQuestionQuery(id: string) {
  try {
    const { data } = await privateAxios.get<EventFormQuestion>(
      `/event-form-questions/${id}`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
}

export const useGetEventFormQuestionQuery = (id: string) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.details(id),
    queryFn: () => getEventFormQuestionQuery(id),
    enabled: !!id,
  });
};

async function getEventFormQuestionsQuery(params?: EventFormQuestionFilters) {
  try {
    const { data } = await privateAxios.get<
      PaginatedResponse<EventFormQuestion>
    >(`/event-form-questions`, { params });
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
}

export const useGetEventFormQuestionsQuery = (
  params?: EventFormQuestionFilters
) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.lists(params),
    queryFn: () => getEventFormQuestionsQuery(params),
  });
};

async function getAllowedEventFormQuestionsQuery(eventId: string) {
  try {
    const { data } = await privateAxios.get<EventFormQuestion[]>(
      `/event-form-questions/${eventId}/allowed-questions`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
}

export const useGetAllowedEventFormQuestionsQuery = (eventId: string) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.allowed(eventId),
    queryFn: () => getAllowedEventFormQuestionsQuery(eventId),
    enabled: !!eventId,
  });
};

async function getEventFormQuestionsOnEventQuery(eventId: string) {
  try {
    const { data } = await privateAxios.get<EventFormQuestionOnEvent[]>(
      `/event-form-questions/${eventId}/questions`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
}

export const useGetEventFormQuestionsOnEventQuery = (eventId: string) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.onEvent(eventId),
    queryFn: () => getEventFormQuestionsOnEventQuery(eventId),
    enabled: !!eventId,
  });
};

const getCashRegisterFormQuestionsQuery = async (eventId: string) => {
  try {
    const { data } = await privateAxios.get<CashRegisterFormQuestionOnEvent[]>(
      `/event-form-questions/${eventId}/cash-register-questions`
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetCashRegisterFormQuestionsQuery = (eventId: string) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.cashRegisterForm(eventId),
    queryFn: () => getCashRegisterFormQuestionsQuery(eventId),
    enabled: !!eventId,
  });
};

async function getEventFormQuestionsForMultipleEventsQuery(eventIds: string[]): Promise<BatchEventFormQuestionsResponse> {
  try {
    const { data } = await privateAxios.post<BatchEventFormQuestionsResponse>(
      `/event-form-questions/batch-questions`,
      { eventIds }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
}

export const useGetEventFormQuestionsForMultipleEventsQuery = (eventIds: string[]) => {
  return useQuery({
    queryKey: eventFormQuestionsQueryKeys.batchOnEvents(eventIds),
    queryFn: () => getEventFormQuestionsForMultipleEventsQuery(eventIds),
    enabled: eventIds.length > 0,
  });
};
